import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import type { User, UserFontSizePreference, UserPreferences, TableColumnPreferences } from '@alpha-edge/shared'
import { api, ApiError } from '../services/api'
import { useAuth } from './use-auth'

// Font-size preference -> html root px value.
// Everything else in the UI is rem-based (including the sidebar,
// which uses rem fractions around 0.875rem for primary nav), so
// changing this value rescales the entire application uniformly.
// Sidebar text stays a step smaller than the main content area
// because its rem multipliers are <1.
export const FONT_SIZE_PX: Record<UserFontSizePreference, number> = {
  sm: 14,
  md: 16,
  lg: 18,
  xl: 22,
}

export const FONT_SIZE_LABELS: Record<UserFontSizePreference, string> = {
  sm: 'Small',
  md: 'Medium (default)',
  lg: 'Large',
  xl: 'Extra Large',
}

interface PreferencesContextValue {
  readonly preferences: UserPreferences
  readonly isLoading: boolean
  readonly setFontSize: (size: UserFontSizePreference) => Promise<void>
  readonly setTableColumns: (tableId: string, prefs: TableColumnPreferences) => void
  readonly getTableColumns: (tableId: string) => TableColumnPreferences | undefined
  readonly error: string | null
}

export const PreferencesContext = createContext<PreferencesContextValue | null>(null)

function applyFontSize(size: UserFontSizePreference | undefined) {
  if (typeof document === 'undefined') return
  const px = size ? FONT_SIZE_PX[size] : FONT_SIZE_PX.md
  document.documentElement.style.setProperty('--font-size-base', `${px}px`)
}

export function PreferencesProvider({ children }: { readonly children: ReactNode }) {
  const { user } = useAuth()
  const [preferences, setPreferences] = useState<UserPreferences>({})
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // No user -- apply default font so Login + onboarding render
    // consistently. Preferences state is already `{}` from initial
    // useState, so we don't need to reset it (avoids setState-in-effect).
    if (!user?.id) {
      applyFontSize(undefined)
      return
    }
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async load + mid-fetch loading state is exactly what this effect is for
    setIsLoading(true)
    api
      .get<User>('/users/me')
      .then((res) => {
        if (cancelled) return
        const prefs = res.data?.preferences ?? {}
        setPreferences(prefs)
        applyFontSize(prefs.fontSize)
      })
      .catch(() => {
        if (!cancelled) applyFontSize(undefined)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [user?.id])

  const setFontSize = useCallback(async (size: UserFontSizePreference) => {
    setError(null)
    setPreferences((prev) => {
      const next = { ...prev, fontSize: size }
      void api
        .put<{ preferences: UserPreferences }>('/users/me/preferences', next)
        .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to save preference'))
      return next
    })
    applyFontSize(size)
  }, [])

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingPrefs = useRef<UserPreferences | null>(null)

  const flushTableColumns = useCallback(() => {
    if (pendingPrefs.current) {
      const toSave = pendingPrefs.current
      pendingPrefs.current = null
      void api
        .put<{ preferences: UserPreferences }>('/users/me/preferences', toSave)
        .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to save preference'))
    }
  }, [])

  const setTableColumns = useCallback(
    (tableId: string, colPrefs: TableColumnPreferences) => {
      setPreferences((prev) => {
        const next = {
          ...prev,
          tableColumns: { ...prev.tableColumns, [tableId]: colPrefs },
        }
        pendingPrefs.current = next
        if (saveTimer.current) clearTimeout(saveTimer.current)
        saveTimer.current = setTimeout(flushTableColumns, 800)
        return next
      })
    },
    [flushTableColumns],
  )

  const getTableColumns = useCallback(
    (tableId: string) => preferences.tableColumns?.[tableId],
    [preferences.tableColumns],
  )

  return (
    <PreferencesContext.Provider
      value={{ preferences, isLoading, setFontSize, setTableColumns, getTableColumns, error }}
    >
      {children}
    </PreferencesContext.Provider>
  )
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext)
  if (!ctx) {
    throw new Error('usePreferences must be used within PreferencesProvider')
  }
  return ctx
}
